import { Card, Icon } from '@ps/isp-web-component-library';
import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  getIsAvailableOpportunity,
  getIsRestrictedMinOddsOpportunity,
  getIsSgmOpportunity,
  getIsSingleAvailableOpportunity,
  getIsSingleOpportunity,
  getIsSingleUnavailableOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';
import { OpportunityDescription } from '@swf/sports/components/OpportunityDescription/OpportunityDescription';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import { useRemoveOpportunityFieldOnUnmount } from '../../../../hooks/useRemoveOpportunityFieldOnUnmount';
import { getIconNameFromOpportunity } from '../../../../utils/getIconNameFromOpportunity';

import styles from './Opportunity.scss';
import BetDetails from './components/BetDetails/BetDetails';
import EachWayCheckbox from './components/EachWayCheckbox/EachWayCheckbox';
import ErrorBanner from './components/ErrorBanner/ErrorBanner';
import Heading from './components/Heading/Heading';
import MinOddsAlert from './components/MinOddsAlert/MinOddsAlert';
import PotentialReturn from './components/PotentialReturn/PotentialReturn';
import PromotionPicker from './components/PromotionPicker/PromotionPicker';
import SgmOpportunity from './components/SgmOpportunity/SgmOpportunity';
import Stake from './components/Stake/Stake';
import StakeAlerts from './components/StakeAlerts/StakeAlerts';
import UnavailableBanner from './components/UnavailableBanner/UnavailableBanner';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Opportunity: React.FC<Props> = ({ opportunity }) => {
  const opportunityRef = React.createRef<HTMLDivElement>();
  const shouldDisplayPromotionPicker = useSelector(select.betSlip.isPromotionPickerEnabled);

  useRemoveOpportunityFieldOnUnmount(opportunity.id);

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isRestrictedMinOddsOpportunity = getIsRestrictedMinOddsOpportunity(opportunity);
  const isSingleOpportunity = getIsSingleOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isSingleUnavailableOpportunity = getIsSingleUnavailableOpportunity(opportunity);
  const isSgmOpportunity = getIsSgmOpportunity(opportunity);

  if (isSgmOpportunity) {
    return <SgmOpportunity opportunity={opportunity} />;
  }

  return (
    <Card
      className={classNames({
        [styles.unavailable]: !isAvailableOpportunity
      })}
      data-testid="bet-slip-opportunity"
      ref={opportunityRef}
    >
      <div className={styles.container}>
        <div className={styles.mainInfo}>
          <div>
            <Icon icon={getIconNameFromOpportunity(opportunity)} size="x1" />
          </div>
          <div className={styles.infoWrapper}>
            <div
              className={classNames(styles.heading, {
                [styles.headingWithBadge]:
                  isSingleAvailableOpportunity && isOddsBoostMarket(opportunity.marketType)
              })}
            >
              <Heading
                opportunity={opportunity}
                opportunityRef={opportunityRef as React.RefObject<HTMLDivElement>}
              />
            </div>

            {isSingleOpportunity && (
              <OpportunityDescription
                {...opportunity}
                isEventInPlay={
                  !getIsSingleUnavailableOpportunity(opportunity)
                    ? opportunity.eventInPlay
                    : undefined
                }
                className={styles.description}
              />
            )}

            <Stake className={styles.stake} opportunity={opportunity} />

            {isAvailableOpportunity && <PotentialReturn opportunity={opportunity} />}
          </div>
        </div>

        {shouldDisplayPromotionPicker && <PromotionPicker opportunity={opportunity} />}

        {isAvailableOpportunity && <StakeAlerts opportunity={opportunity} />}

        {isRestrictedMinOddsOpportunity && <MinOddsAlert minOdds={opportunity.minOdds} />}

        {isAvailableOpportunity && opportunity.eachWayAvailable && (
          <div>
            <EachWayCheckbox
              fieldName={opportunity.id}
              placeTerms={isSingleOpportunity ? opportunity.placeTerms : undefined}
            />

            <BetDetails opportunity={opportunity} />
          </div>
        )}
      </div>

      {isSingleUnavailableOpportunity && <UnavailableBanner runnerId={opportunity.runnerId} />}
      <ErrorBanner fieldName={opportunity.id} />
    </Card>
  );
};

export default Opportunity;
